export interface User {
  id: string
  email: string
  name: string
  role: string
  walletAddress?: string
  botId?: string
  bot?: Bot
}

export interface Bot {
  id: string
  userId: string
  bio: string | null
  skills: string[]
  avatarUrl: string | null
  walletAddress: string | null
  rating: number
  jobsCompleted: number
  totalEarned: number
  successRate: number
  createdAt: string
  updatedAt: string
  user?: { id: string; name: string }
}

export interface Job {
  id: string
  posterId: string
  title: string
  description: string
  reward: number
  deadline: string | null
  status: JobStatus
  mode: JobMode
  category: string | null
  tags: string[]
  attachments: string[]
  createdAt: string
  updatedAt: string
  poster: { id: string; name: string; email?: string }
  applications?: Application[]
  submissions?: Submission[]
  _count?: {
    applications: number
    submissions: number
  }
}

export type JobStatus = 'PENDING_REVIEW' | 'OPEN' | 'APPLIED' | 'IN_PROGRESS' | 'REVIEW' | 'COMPLETED' | 'CANCELLED'
export type JobMode = 'INSTANT' | 'PROPOSAL'
export type SubmissionStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'REVISION_REQUESTED'
export type NotificationType =
  | 'JOB_CREATED'
  | 'JOB_APPLICATION'
  | 'JOB_APPLICATION_ACCEPTED'
  | 'JOB_APPLICATION_REJECTED'
  | 'SUBMISSION_RECEIVED'
  | 'SUBMISSION_APPROVED'
  | 'SUBMISSION_REJECTED'
  | 'REVISION_REQUESTED'
  | 'REVIEW_RECEIVED'
  | 'PAYMENT_RECEIVED'
  | 'SYSTEM'
  | 'PRODUCT_SUBMITTED'
  | 'PRODUCT_APPROVED'
  | 'PRODUCT_REJECTED'
  | 'PRODUCT_PURCHASED'
  | 'PRODUCT_SOLD'

export interface Application {
  id: string
  jobId: string
  botId: string
  message: string | null
  stakePercent: number
  accepted: boolean
  createdAt: string
  updatedAt: string
  job?: Job
  bot?: Bot & { user: { id: string; name: string } }
}

export interface Submission {
  id: string
  jobId: string
  botId: string
  content: string
  attachments: string[]
  status: SubmissionStatus
  revision: number
  feedback: string | null
  createdAt: string
  updatedAt: string
  job?: Job
  bot?: Bot & { user: { id: string; name: string } }
  reviews?: Review[]
}

export interface Review {
  id: string
  submissionId: string
  giverId: string
  receiverId: string
  rating: number
  comment: string | null
  createdAt: string
}

export interface Notification {
  id: string
  userId: string
  type: NotificationType
  title: string
  message: string
  data: Record<string, unknown> | null
  read: boolean
  createdAt: string
}

// Generic paginated response - use specific interfaces for type safety
export interface PaginatedResponse<T> {
  total: number
  page: number
  limit: number
  totalPages: number
  data?: T[]
  items?: T[]
  jobs?: T[]
  products?: T[]
  notifications?: T[]
  purchases?: T[]
  reviews?: T[]
  bots?: T[]
}

export interface LeaderboardEntry {
  rank: number
  id: string
  userId: string
  name: string
  bio: string | null
  skills: string[]
  avatarUrl: string | null
  rating: number
  jobsCompleted: number
  totalEarned: number
  successRate: number
}

// For filtering (job list page) - empty means "All"
export const jobCategoriesFilter = [
  { value: '', label: 'All Categories' },
  { value: 'development', label: 'Development' },
  { value: 'data-analysis', label: 'Data Analysis' },
  { value: 'writing', label: 'Writing & Content' },
  { value: 'design', label: 'Design' },
  { value: 'security', label: 'Security' },
  { value: 'research', label: 'Research' },
  { value: 'automation', label: 'Automation' },
  { value: 'other', label: 'Other' },
] as const

// For job creation - empty means "Select a category"
export const jobCategories = [
  { value: '', label: 'Select a category' },
  { value: 'development', label: 'Development' },
  { value: 'data-analysis', label: 'Data Analysis' },
  { value: 'writing', label: 'Writing & Content' },
  { value: 'design', label: 'Design' },
  { value: 'security', label: 'Security' },
  { value: 'research', label: 'Research' },
  { value: 'automation', label: 'Automation' },
  { value: 'other', label: 'Other' },
] as const

// Product types
export type ProductStatus = 'DRAFT' | 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'DELISTED'
export type ProductCategory = 'SKILL' | 'PROMPT' | 'CONFIG' | 'WORKFLOW' | 'OTHER'
export type ProductPlatform = 'OPENCLAW' | 'CLAUDE_COWORK' | 'CODEX_APP' | 'GENERIC' | 'OTHER'

export interface Product {
  id: string
  sellerId: string
  title: string
  description: string
  content?: string
  price: number
  status: ProductStatus
  category: ProductCategory
  platform: ProductPlatform
  tags: string[]
  previewSnippet: string | null
  stockQuantity: number | null // null means unlimited
  totalSales: number
  totalRevenue: number
  averageRating: number
  totalReviews: number
  createdAt: string
  updatedAt: string
  approvedAt: string | null
  seller: { id: string; name: string }
  hasPurchased?: boolean
  reviews?: ProductReview[]
  userReview?: CustomerProductReview | null
  _count?: {
    purchases: number
    reviews: number
  }
}

export interface ProductReview {
  id: string
  productId: string
  reviewJobId: string
  botId: string | null
  decision: 'PENDING' | 'APPROVED' | 'REJECTED'
  reason: string | null
  reviewedAt: string | null
  createdAt: string
}

export interface ProductPurchase {
  id: string
  productId: string
  buyerId: string
  price: number
  platformFee: number
  sellerRevenue: number
  createdAt: string
  product: Product
}

export interface CustomerProductReview {
  id: string
  productId: string
  buyerId: string
  rating: number
  comment: string | null
  createdAt: string
  updatedAt: string
  buyer: { id: string; name: string }
}

export const productCategories = [
  { value: 'SKILL', label: 'Skill Files' },
  { value: 'PROMPT', label: 'Prompt Templates' },
  { value: 'CONFIG', label: 'Configurations' },
  { value: 'WORKFLOW', label: 'Workflows' },
  { value: 'OTHER', label: 'Other' },
] as const

export const productCategoriesFilter = [
  { value: '', label: 'All Categories' },
  { value: 'SKILL', label: 'Skill Files' },
  { value: 'PROMPT', label: 'Prompt Templates' },
  { value: 'CONFIG', label: 'Configurations' },
  { value: 'WORKFLOW', label: 'Workflows' },
  { value: 'OTHER', label: 'Other' },
] as const

export const productPlatforms = [
  { value: 'OPENCLAW', label: 'openClaw' },
  { value: 'CLAUDE_COWORK', label: 'Claude CoWork' },
  { value: 'CODEX_APP', label: 'Codex App' },
  { value: 'GENERIC', label: 'Generic (All Agents)' },
  { value: 'OTHER', label: 'Other' },
] as const

export const productPlatformsFilter = [
  { value: '', label: 'All Platforms' },
  { value: 'OPENCLAW', label: 'openClaw' },
  { value: 'CLAUDE_COWORK', label: 'Claude CoWork' },
  { value: 'CODEX_APP', label: 'Codex App' },
  { value: 'GENERIC', label: 'Generic (All Agents)' },
  { value: 'OTHER', label: 'Other' },
] as const

// Notification Channel types
export type NotificationChannel = 'TELEGRAM' | 'WHATSAPP'

export interface UserNotificationChannel {
  id: string
  userId: string
  channel: NotificationChannel
  verified: boolean
  enabled: boolean
  createdAt: string
  updatedAt: string
}
